// The borderless large view of an image in the body. Clicking an image does not
// navigate away; it shows the image large in a borderless layer (user request).
//
// ## Not drawn inside the node view
//
// The image node view lives inside the contenteditable. Inserting an
// `aria-modal` dialog there is a large DOM change ProseMirror does not manage.
// So the node view only dispatches a CustomEvent (`openFileLightbox`), and the
// layer is drawn by a host mounted once next to the editor or the static body
// (`MediaLayers`).
//
// ## Portaled to body
//
// The theme lives on `<html data-theme>` in ui-core, so body is inside it (the
// old web had to portal to the nearest `[data-theme]` instead; that reason is
// gone). Portaling still matters: `position: fixed` is relative to the nearest
// ancestor with a `transform` / `translate` / `scale`, and a sliding dock that
// always carries a translate once trapped the viewer inside its box.
//
// ## No frame, the whole screen
//
// Not the Modal's surface (background, border, shadow, radius, max width): what
// is shown is **the image itself**, one `<img>` fitted to the screen (never
// stretched) over a dark see-through backdrop, like a modal's. The controls
// float over the image as in Slack's viewer: the name top left, close top
// right, "…" bottom right. Tapping the viewer shows and hides them (user
// request). With a mouse they fade in when the mouse moves and fade out after
// 1.5 s without movement (user request). A finger tap does not close (taps
// show and hide the controls); closing is ×, Escape, and — with a mouse — a
// click anywhere, on the image or the space around it, at once, without
// waiting to see whether it is a double click (waiting felt like lag; user
// request).
//
// Where the window draws its own controls over the top corners (the mac
// desktop app's traffic lights at the left, the buttons of Windows and Linux at
// the right), the name and the buttons keep clear of them.
//
// The "…" opens the menu at the button; on a phone (the host has the phone's
// screens) a drawer from the bottom, as in the old web (`PhoneSlot`
// MEDIA_MORE_SLOT, src/phone/media.tsx).
//
// ## Pinch to zoom
//
// Zooming moves the image only; the page's own zoom is stopped
// (`touch-action: none`) (user request: pinch on phones and Mac trackpads).
// Three ways in:
// - Fingers (pointer events): pinch with two, pan with one while zoomed,
//   double tap to zoom in / back
// - Mac trackpad: Chrome / Firefox send a wheel with `ctrlKey`, Safari sends
//   the non-standard `gesturechange`
// - Mouse: Ctrl (or ⌘) with the wheel zooms; the wheel alone, and two fingers
//   on a trackpad, move around the zoomed picture (the person, 2026-11-02:
//   the wheel zooming was in the way). Drag to pan while zoomed. A click
//   closes, so there is no double click zoom: − and + at the bottom right
//   (and the − + 0 keys) zoom instead, toward the middle
// The maths is in zoom.ts (pure functions).
//
// ## Smooth motion
//
// The zoom and pan felt not smooth (user feedback), so:
// - finger moves write the `<img>`'s `transform` directly, not through a React render
// - it can be pulled out of range (smaller than fit, past the edges), getting
//   heavier, and springs back on release
// - a flick keeps gliding a little at its speed before it stops
// - double tap zoom and the spring back are animated
//
// ## Stepping through the images around
//
// The images of the same message, body or document step left and right
// (gallery.ts gathers them when the viewer opens; user request, 2026-11-02).
// With two or more, "3 / 7" shows beside ×. With a mouse, ‹ › at the sides
// come and go with the controls (none past the first or the last), and the
// trackpad's two fingers sideways step while not zoomed in; ← → keys too.
// With fingers, a swipe sideways at fit size (the picture follows, and one
// with nothing beyond pulls heavier and springs back). Only the next image in
// the way one goes is read ahead.
//
// ## In an app that shows images itself
//
// When the host says it shows them (`ShellHost.viewer`, the iOS app), nothing
// is drawn: what was pressed and the images around go to the app
// (app-viewer.ts). Files not sent yet (`blob:`) still open here.
import { m } from "@trameria/i18n";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal, flushSync } from "react-dom";
import { MeAppContext } from "../../../app/context";
import { useViewportWidth } from "../../../shell/layout";
import { useOutsideCover } from "../../../shell/outside/cover";
import {
  PhoneSlot,
  usePhone,
  useStowHostPartsWhile,
} from "../../../shell/phone";
import { useWindowCorner } from "../../../shell/title-bar";
import { Icon } from "../../../ui/icon";
import { Popover } from "../../../ui/popover";
import { canHand, handToApp } from "./app-viewer";
import { FloatingIconButton } from "./floating-icon-button";
import { claim, follow, quietFocus, returnFocusTo } from "./focus";
import { type GalleryItem, gatherGallery } from "./gallery";
import { MEDIA_MORE_SLOT, type MediaMoreProps } from "./media-more-port";
import {
  clampZoom,
  easeOut,
  flingZoom,
  mixZoom,
  rubberZoom,
  settleZoom,
  ZOOM_IDENTITY,
  ZOOM_MAX,
  ZOOM_STEP,
  type Zoom,
  zoomAt,
  zoomAtFree,
} from "./zoom";

/** The signal that opens the image viewer (node view / static body → host). */
export const FILE_LIGHTBOX_EVENT = "trameria:file-lightbox";

export interface FileLightboxDetail {
  /** The image URL (without the download flag). */
  src: string;
  /** Read aloud and the `alt` (the file name given by the uploader). */
  name: string;
  /** The download URL. Without it the "…" menu has no download item. */
  download?: string;
  /**
   * Who put it there (an image attached to a message), shown top left over
   * the name; `at` is the time as the caller shows it.
   */
  author?: string;
  at?: string;
}

/**
 * Dispatches the signal from `from` (the pressed element). **It bubbles**: the
 * hosts listen on window, and the focus returns to `from` on close.
 */
export function openFileLightbox(
  from: Element,
  detail: FileLightboxDetail,
): void {
  from.dispatchEvent(
    new CustomEvent<FileLightboxDetail>(FILE_LIGHTBOX_EVENT, {
      detail,
      bubbles: true,
    }),
  );
}

/**
 * Layer order: above popovers (50) — the viewer covers the screen and its own
 * menu is drawn inside it — but below toasts (55) and tooltips (60), so a
 * message raised meanwhile stays visible.
 */
export const MEDIA_LAYER_Z = 54;

interface Open {
  /** The images to step through (the pressed one alone when none are around). */
  items: GalleryItem[];
  /** The one pressed. */
  index: number;
  /** Where the focus goes back on close (the pressed element). */
  opener: Element | null;
  /**
   * Whether the opener showed a focus ring when the viewer opened (opened by
   * keyboard). If not (click / tap), the focus handed back shows no ring either
   * (`quietFocus`).
   */
  ring: boolean;
  /** Each opening its own (a fresh viewer, even for the same image). */
  seq: number;
}

/** How the image shown came in: stepped by a swipe (from further) or a press. */
export interface Enter {
  by: 1 | -1;
  swipe: boolean;
}

/** Reads `src` ahead (into the browser's cache), once per viewer. */
function readAhead(src: string | undefined, read: Set<string>) {
  if (!src || read.has(src) || typeof Image === "undefined") return;
  read.add(src);
  new Image().src = src;
}

/** The host of the image viewer. Mounted once per screen by `MediaLayers`. */
export function FileLightbox() {
  const [open, setOpen] = useState<Open | null>(null);
  const [at, setAt] = useState(0);
  const [enter, setEnter] = useState<Enter | null>(null);
  const atRef = useRef(at);
  atRef.current = at;
  const read = useRef(new Set<string>());
  const panel = useRef<HTMLDivElement>(null);
  /** The key handler of the inner viewer (keys are taken on the panel, so they work with the focus on the panel itself). */
  const onKey = useRef<((e: ReactKeyboardEvent) => void) | null>(null);
  const host = useContext(MeAppContext)?.extensions.shellHost ?? null;
  // Read inside the open signal's listener (set up once).
  const hostRef = useRef(host);
  hostRef.current = host;
  const close = useCallback(() => setOpen(null), []);
  useStowHostPartsWhile(open !== null);
  // The ground (`bg-black/40`) over the host's parts too; a click there closes, as a click beside the image.
  useOutsideCover(open !== null, 0.4, close);

  // frontend-development: not a fetch; listens for the open signal on window.
  useEffect(() => {
    let seq = 0;
    const onOpen = (e: Event) => {
      if (!claim(e)) return;
      const detail = (e as CustomEvent<FileLightboxDetail>).detail;
      if (!detail) return;
      const opener = e.target instanceof Element ? e.target : null;
      const { items, index } = gatherGallery(opener, detail);
      const shellHost = hostRef.current;
      if (canHand(shellHost, detail.src)) {
        handToApp(shellHost, { kind: "image", items, index, opener });
        return;
      }
      read.current = new Set([detail.src]);
      readAhead(items[index + 1]?.src, read.current);
      setAt(index);
      setEnter(null);
      setOpen({
        items,
        index,
        opener,
        ring: opener?.matches(":focus-visible") ?? false,
        seq: ++seq,
      });
    };
    window.addEventListener(FILE_LIGHTBOX_EVENT, onOpen);
    return () => window.removeEventListener(FILE_LIGHTBOX_EVENT, onOpen);
  }, []);

  const count = open?.items.length ?? 0;
  /** Steps to the image before (-1) or after (1); false at either end. */
  const step = useCallback(
    (by: 1 | -1, swipe = false): boolean => {
      const next = atRef.current + by;
      if (!open || next < 0 || next >= open.items.length) return false;
      atRef.current = next;
      setAt(next);
      setEnter({ by, swipe });
      readAhead(open.items[next + by]?.src, read.current);
      return true;
    },
    [open],
  );

  // Opening moves the focus into the panel; closing returns it to the opener
  // (or the image stepped to, which is where one is now) only when the focus
  // is vacant (`returnFocusTo`).
  // frontend-development: not a fetch; moves the focus in and back out.
  useEffect(() => {
    if (!open) return;
    const el = panel.current;
    if (!el) return;
    if (!el.contains(document.activeElement)) el.focus({ preventScroll: true });
    return () => {
      const last = open.items[atRef.current]?.el;
      const back =
        atRef.current !== open.index &&
        last instanceof HTMLElement &&
        last.isConnected
          ? last
          : open.opener;
      returnFocusTo(el, back);
      // Marked before paint (same task), so the ring never shows.
      if (!open.ring && back && document.activeElement === back)
        quietFocus(back);
    };
  }, [open]);

  if (!open) return null;
  const item = open.items[at] ?? (open.items[0] as GalleryItem);
  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={item.name}
      tabIndex={-1}
      data-media-viewer=""
      style={{ zIndex: MEDIA_LAYER_Z }}
      className="fixed inset-0 bg-black/40 outline-none"
      onKeyDown={(e) => {
        onKey.current?.(e);
        // While the viewer is open, keys belong to it: the app's shortcuts
        // behind it (a document listener that does not look at
        // `defaultPrevented`) must not run.
        e.stopPropagation();
      }}
    >
      {/* Opening again drops the zoom and the controls' state (`key`). */}
      <Viewer
        key={open.seq}
        item={item}
        at={at}
        count={count}
        enter={enter}
        step={step}
        panel={panel}
        onKey={onKey}
        onClose={close}
      />
    </div>,
    document.body,
  );
}

interface MenuItemSpec {
  id: string;
  label: string;
  run(): void;
}

function Viewer({
  item: open,
  at,
  count,
  enter,
  step,
  panel,
  onKey,
  onClose,
}: {
  /** The image shown. */
  item: GalleryItem;
  /** Its place among `count` images. */
  at: number;
  count: number;
  /** How it came in (null: opened on it). */
  enter: Enter | null;
  step: (by: 1 | -1, swipe?: boolean) => boolean;
  panel: RefObject<HTMLDivElement | null>;
  onKey: RefObject<((e: ReactKeyboardEvent) => void) | null>;
  onClose: () => void;
}) {
  /** − and + (and their keys) zoom the stage shown. */
  const zoomControl = useRef<ZoomControl | null>(null);
  const [atMin, setAtMin] = useState(true);
  const [atMax, setAtMax] = useState(false);
  /** One trackpad swipe steps once, its glide after the fingers lift included. */
  const wheelSwipe = useRef<WheelSwipe>({
    sum: 0,
    last: -Infinity,
    done: false,
  });
  const phone = useViewportWidth() <= PHONE_MAX;
  // The phone's screens draw "…" as a drawer (only a host that has them).
  const usesDrawer = usePhone();
  const corner = useWindowCorner();
  const [chrome, setChrome] = useState(true);
  const layer = useRef<HTMLDivElement>(null);
  /** Whether the menu was open when this press began (the press is for closing the menu). */
  const tapDismissesMenu = useRef(false);
  /**
   * Before hiding, moves the focus from a button to the panel: focus left
   * inside an inert subtree drops to body, and then keys other than Escape
   * leak to the page behind.
   */
  const hideChrome = useCallback(() => {
    if (layer.current?.contains(document.activeElement))
      panel.current?.focus({ preventScroll: true });
    setChrome(false);
  }, [panel]);
  const toggleChrome = () => {
    if (tapDismissesMenu.current) return;
    if (chrome) hideChrome();
    else setChrome(true);
  };
  const moreButton = useRef<HTMLButtonElement>(null);
  const menuList = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  // Read by listeners and timers without waiting for a render.
  const menuOpenRef = useRef(menuOpen);
  menuOpenRef.current = menuOpen;

  // Whether a press began with the menu open. Read on window in the capture
  // phase: the Popover closes on a document capture listener, and React
  // re-renders between the two listeners, so a React handler would already
  // see the menu closed.
  // frontend-development: not a fetch; reads the state at the start of a press.
  useEffect(() => {
    const onDown = () => {
      tapDismissesMenu.current = menuOpenRef.current;
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => window.removeEventListener("pointerdown", onDown, true);
  }, []);

  // On mouse devices the controls appear when the mouse moves and hide after
  // IDLE_HIDE_MS without movement (user request: fade in on move, out after
  // 1.5 s). Not while the menu is open, a button is hovered, or a button has
  // the keyboard focus. Finger taps still show and hide them.
  const idleTimer = useRef<number | null>(null);
  const scheduleHide = useCallback(() => {
    if (idleTimer.current !== null) clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      idleTimer.current = null;
      const el = layer.current;
      const busy =
        menuOpenRef.current ||
        el?.querySelector("button:hover") ||
        el?.querySelector(":focus-visible");
      if (busy) scheduleHide();
      else hideChrome();
    }, IDLE_HIDE_MS);
  }, [hideChrome]);
  const onMouseMove = (e: ReactPointerEvent) => {
    if (e.pointerType !== "mouse") return;
    setChrome(true);
    scheduleHide();
  };
  // Shown right after opening; on mouse devices hidden when the mouse rests.
  // frontend-development: not a fetch; hides the controls after a while.
  useEffect(() => {
    if (window.matchMedia?.("(hover: hover) and (pointer: fine)").matches)
      scheduleHide();
    return () => {
      if (idleTimer.current !== null) clearTimeout(idleTimer.current);
    };
  }, [scheduleHide]);

  // Keys, taken on the panel. Tab stays on the panel's buttons (never leaks to
  // the page behind). The menu does not trap Tab: Tab closes it and returns to
  // "…". Escape closes the viewer; while the menu is open the Popover takes
  // Escape first (its document capture listener) to close only the menu.
  onKey.current = (e) => {
    if (e.key === "Escape") {
      if (menuOpenRef.current) return;
      e.preventDefault();
      onClose();
      return;
    }
    if (
      (e.key === "ArrowLeft" || e.key === "ArrowRight") &&
      !menuOpenRef.current &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey
    ) {
      e.preventDefault();
      step(e.key === "ArrowLeft" ? -1 : 1);
      return;
    }
    if (!menuOpenRef.current && !e.altKey && !e.metaKey && !e.ctrlKey) {
      const zoom =
        e.key === "+" || e.key === "="
          ? () => zoomControl.current?.zoomBy(BUTTON_ZOOM)
          : e.key === "-"
            ? () => zoomControl.current?.zoomBy(1 / BUTTON_ZOOM)
            : e.key === "0"
              ? () => zoomControl.current?.reset()
              : null;
      if (zoom) {
        e.preventDefault();
        zoom();
        return;
      }
    }
    if (e.key !== "Tab") return;
    e.preventDefault();
    if (menuOpenRef.current) {
      setMenuOpen(false);
      moreButton.current?.focus();
      return;
    }
    const root = panel.current;
    if (!root) return;
    // Hidden controls are shown first (without waiting for inert to go).
    if (!chrome) flushSync(() => setChrome(true));
    const buttons = [
      ...root.querySelectorAll<HTMLElement>("[data-lightbox-controls] button"),
    ].filter((b) => !b.closest("[inert]"));
    if (buttons.length === 0) return;
    const at = buttons.indexOf(document.activeElement as HTMLElement);
    const next = e.shiftKey
      ? at <= 0
        ? buttons.length - 1
        : at - 1
      : at < 0 || at === buttons.length - 1
        ? 0
        : at + 1;
    buttons[next]?.focus();
  };

  // Escape when the focus is not in the panel at all (keys from inside are
  // handled above and stopped there).
  // frontend-development: not a fetch; closes on Escape from outside the panel.
  useEffect(() => {
    const onDocKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || menuOpenRef.current)
        return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onDocKey);
    return () => document.removeEventListener("keydown", onDocKey);
  }, [onClose]);

  const items: MenuItemSpec[] = [
    ...(open.download
      ? [
          {
            id: "download",
            label: m.media_download(),
            run: () => follow(open.download as string, false),
          },
        ]
      : []),
    {
      id: "tab",
      label: m.media_open_in_tab(),
      run: () => follow(open.src, true),
    },
  ];
  const focusItem = (step: 1 | -1 | 0) => {
    const all = [
      ...(menuList.current?.querySelectorAll<HTMLButtonElement>(
        "[role=menuitem]",
      ) ?? []),
    ];
    if (all.length === 0) return;
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    const next = step === 0 ? 0 : (at + step + all.length) % all.length;
    all[next]?.focus();
  };

  // The layer itself lets fingers through (to the image below); while shown,
  // only the buttons take them. Fades both ways (out a little slower).
  const shownClass = chrome
    ? "duration-300 [&_button]:pointer-events-auto"
    : "duration-500 opacity-0";
  return (
    <div className="absolute inset-0" onPointerMove={onMouseMove}>
      {/* Another image drops the zoom (`key`); the controls stay as they are. */}
      <ZoomStage
        key={open.src}
        src={open.src}
        name={open.name}
        nativePixels={!phone}
        enter={enter}
        canStep={(by) => at + by >= 0 && at + by < count}
        onStep={step}
        wheelSwipe={wheelSwipe}
        control={zoomControl}
        onLimits={(min, max) => {
          setAtMin(min);
          setAtMax(max);
        }}
        onTap={toggleChrome}
        onMouseClick={() => {
          if (!tapDismissesMenu.current) onClose();
        }}
      />
      {/* The controls. Hidden ones can't be pressed or reached by Tab (inert). */}
      <div
        ref={layer}
        data-lightbox-controls=""
        inert={!chrome || undefined}
        className={`pointer-events-none absolute inset-0 transition-opacity ease-out motion-reduce:transition-none ${shownClass}`}
      >
        <div
          className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 px-3 pt-[max(12px,env(safe-area-inset-top))]"
          style={
            corner
              ? {
                  paddingLeft: corner.left || undefined,
                  paddingRight: corner.right || undefined,
                }
              : undefined
          }
        >
          <div className="media-floating flex h-12 min-w-0 flex-col justify-center rounded-[24px] px-4">
            {open.author ? (
              <>
                <div className="truncate text-[13.5px] leading-[1.4] text-fg">
                  {open.author}
                  {open.at && (
                    <span className="ml-2 text-muted">{open.at}</span>
                  )}
                </div>
                <div className="truncate text-[11.5px] leading-[1.4] text-muted">
                  {open.name}
                </div>
              </>
            ) : (
              <div className="truncate text-[13.5px] leading-[1.4] text-fg">
                {open.name}
              </div>
            )}
          </div>
          <div className="flex flex-none items-center gap-2">
            {count > 1 && (
              <div
                role="status"
                aria-label={m.media_image_position({
                  n: at + 1,
                  count,
                })}
                className="media-floating flex h-12 items-center rounded-[24px] px-4 text-[13.5px] text-fg tabular-nums"
              >
                {at + 1} / {count}
              </div>
            )}
            <FloatingIconButton label={m.media_close()} onClick={onClose}>
              <Icon kind="close" size={20} />
            </FloatingIconButton>
          </div>
        </div>
        {/* Before and after, with a mouse (fingers swipe instead). Not past
            the first or the last. */}
        {at > 0 && (
          <div className="absolute top-1/2 left-0 -translate-y-1/2 px-3 pointer-coarse:hidden">
            <FloatingIconButton
              label={m.media_image_previous()}
              onClick={() => step(-1)}
            >
              <Icon kind="back" size={20} />
            </FloatingIconButton>
          </div>
        )}
        {at < count - 1 && (
          <div className="absolute top-1/2 right-0 -translate-y-1/2 px-3 pointer-coarse:hidden">
            <FloatingIconButton
              label={m.media_image_next()}
              onClick={() => step(1)}
            >
              <Icon kind="forward" size={20} />
            </FloatingIconButton>
          </div>
        )}
        <div className="absolute bottom-0 right-0 flex gap-2 px-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          {/* With a mouse (fingers pinch instead). */}
          <div className="flex gap-2 pointer-coarse:hidden">
            <FloatingIconButton
              label={m.media_zoom_out()}
              disabled={atMin}
              onClick={() => zoomControl.current?.zoomBy(1 / BUTTON_ZOOM)}
            >
              <Icon kind="minus" size={20} />
            </FloatingIconButton>
            <FloatingIconButton
              label={m.media_zoom_in()}
              disabled={atMax}
              onClick={() => zoomControl.current?.zoomBy(BUTTON_ZOOM)}
            >
              <Icon kind="plus" size={20} />
            </FloatingIconButton>
          </div>
          <FloatingIconButton
            ref={moreButton}
            label={m.media_more_actions()}
            hasPopup="menu"
            expanded={menuOpen}
            onClick={() => {
              setMenuOpen((v) => !v);
              if (!usesDrawer) requestAnimationFrame(() => focusItem(0));
            }}
          >
            <Icon kind="more" size={20} />
          </FloatingIconButton>
        </div>
      </div>
      {/* On a phone a drawer from the bottom (the old web's), drawn by the
          phone kit; else the menu at the button. Both inside the panel (no
          portal): the panel's layer orders them above the image. */}
      {usesDrawer ? (
        <PhoneSlot<MediaMoreProps>
          name={MEDIA_MORE_SLOT}
          props={{
            open: menuOpen,
            label: m.media_image_actions(),
            items,
            onClose: closeMenu,
            opener: moreButton,
          }}
        />
      ) : null}
      <Popover
        anchor={moreButton}
        open={menuOpen && !usesDrawer}
        onClose={closeMenu}
        align="end"
      >
        <div
          ref={menuList}
          role="menu"
          aria-label={m.media_image_actions()}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              focusItem(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              focusItem(-1);
            }
          }}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="flex h-8 w-full cursor-pointer items-center px-3 text-left text-[13px] text-body hover:bg-hover focus:bg-hover focus:outline-none pointer-coarse:h-11 pointer-coarse:text-[14px]"
              onClick={() => {
                moreButton.current?.focus({ preventScroll: true });
                setMenuOpen(false);
                item.run();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </Popover>
    </div>
  );
}

/** Below this width the image is fitted to the screen instead of shown at device pixels. */
const PHONE_MAX = 600;
/** A small image is drawn at most this many device pixels per image pixel (desktop). */
const SMALL_IMAGE_MAX = 2;
/** Without mouse movement for this long the controls hide (mouse devices). */
const IDLE_HIDE_MS = 1500;
/** Interval and distance of a second tap. A first tap takes effect this late. */
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_PX = 30;
/** Moving more than this makes it a move, not a tap. */
const MOVE_SLOP_PX = 4;
/** The flick speed is measured over this last stretch before release. */
const FLING_WINDOW_MS = 80;
/** Slower releases (px/ms) do not glide. */
const FLING_MIN_SPEED = 0.15;
/** Duration of double tap zoom and spring back (ms); a glide takes {@link FLING_ANIM_MS}. */
const ANIM_MS = 260;
const FLING_ANIM_MS = 420;
/** A trackpad swipe is over once its wheel events stop for this long (its glide included). */
const WHEEL_GAP_MS = 220;
/** Sideways wheel travel (px) that steps to the next image. */
const WHEEL_STEP_PX = 40;
/** A finger swipe steps past this share of the screen's width, or at {@link SWIPE_SPEED}. */
const SWIPE_SHARE = 0.18;
const SWIPE_SPEED = 0.35;
/** The image swiped away leaves in this long; the next one comes in in {@link ENTER_MS}. */
const SWIPE_OUT_MS = 160;
const ENTER_MS = 220;
/** Where the next image comes in from: this far for a press, this share of the width for a swipe. */
const ENTER_PRESS_PX = 48;
const ENTER_SWIPE_SHARE = 0.3;
/** Pulled toward an end with nothing beyond, the image moves this share of the finger. */
const END_PULL = 0.3;
/** Most a wheel event zooms (its log): a mouse notch with Ctrl about 20%. */
const WHEEL_ZOOM_MAX = 0.2;
/** What − and + zoom by, toward the middle. */
const BUTTON_ZOOM = 1.5;

/** What the viewer's − and + (and their keys) do to the stage shown. */
interface ZoomControl {
  zoomBy(factor: number): void;
  reset(): void;
}

/** A trackpad's sideways swipe, kept across the images it steps through. */
interface WheelSwipe {
  /** Sideways travel since it began. */
  sum: number;
  /** The time of its last wheel event. */
  last: number;
  /** It has stepped (the rest of it, its glide, does nothing). */
  done: boolean;
}

/** Safari's `GestureEvent` (not in the DOM types). */
interface GestureLike extends UIEvent {
  scale: number;
  clientX: number;
  clientY: number;
}

/** The start (one or two fingers), taken again whenever the finger count changes. */
interface Grab {
  /** Raw value at the start (before clamping). */
  zoom: Zoom;
  /** Midpoint of two fingers, or the one finger (screen coordinates). */
  x: number;
  y: number;
  /** Distance between two fingers (0 for one). */
  dist: number;
}

function reducedMotion(): boolean {
  return (
    typeof requestAnimationFrame !== "function" ||
    (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false)
  );
}

function ZoomStage({
  src,
  name,
  onTap,
  onMouseClick,
  nativePixels,
  enter,
  canStep,
  onStep,
  wheelSwipe,
  control,
  onLimits,
}: {
  src: string;
  name: string;
  /** How it came in: it slides in from that side and fades in (null: opened on it). */
  enter: Enter | null;
  /** Whether there is an image before (-1) or after (1). */
  canStep: (by: 1 | -1) => boolean;
  onStep: (by: 1 | -1, swipe?: boolean) => boolean;
  wheelSwipe: RefObject<WheelSwipe>;
  /** Where − and + reach this stage. */
  control: RefObject<ZoomControl | null>;
  /** Whether it is at fit size and at the most zoom (as it changes). */
  onLimits: (min: boolean, max: boolean) => void;
  /**
   * An image that fits is drawn larger than its pixels, up to two device
   * pixels per image pixel, never past the screen (desktop). Measured in
   * device pixels: in CSS pixels a Retina screen doubled it, and a screenshot
   * showed larger than what was captured (user request: actual size); a small
   * one then looked too small, so up to 200% (the person, 2026-11-02).
   * Images that don't fit are fitted, and phones always fit.
   */
  nativePixels: boolean;
  /** A finger pressed once without moving (not a double tap). */
  onTap: () => void;
  /**
   * The mouse clicked without moving, on the image or the space around it
   * (instead of `onTap`, at once, without waiting for a double click). On a
   * desktop closing is more natural than toggling the controls (user request).
   * Finger taps still toggle. In exchange a mouse can't double click to zoom
   * (pinch on the trackpad, or the wheel).
   */
  onMouseClick: () => void;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const img = useRef<HTMLImageElement>(null);
  /** The position shown. */
  const shown = useRef<Zoom>(ZOOM_IDENTITY);
  /** The fingers' raw position (before the out-of-range pull is shrunk). Equals `shown` once released. */
  const raw = useRef<Zoom>(ZOOM_IDENTITY);
  /** Whether zoomed in (re-rendered only for the cursor, when crossing 1). */
  const [zoomed, setZoomed] = useState(false);
  const anim = useRef(0);
  /** Whether an animation (`glide`) is running. */
  const animating = useRef(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const grab = useRef<Grab | null>(null);
  /** Whether the press moved (then it is not a tap). */
  const moved = useRef(false);
  const downAt = useRef<{ x: number; y: number } | null>(null);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);
  const tapTimer = useRef<number | null>(null);
  /** Recent positions of a one-finger move (for the flick speed). */
  const samples = useRef<{ x: number; y: number; t: number }[]>([]);
  /**
   * A finger's move at fit size, once it has gone far enough to tell: sideways
   * swipes to another image ("x"), any other way does nothing ("none").
   */
  const swipe = useRef<"x" | "none" | null>(null);
  /** Waits for the image swiped away to leave before stepping. */
  const outTimer = useRef<number | null>(null);

  /** The image's size before zooming (transform does not change offsetWidth). */
  const box = useCallback(
    () => ({
      width: img.current?.offsetWidth ?? 0,
      height: img.current?.offsetHeight ?? 0,
    }),
    [],
  );
  /** The stage's (screen's) size; a zoomed picture's edges may come up to its edges. */
  const view = useCallback(
    () => ({
      width: stage.current?.clientWidth || box().width,
      height: stage.current?.clientHeight || box().height,
    }),
    [box],
  );
  /** A screen point, measured from the image's center (= the stage's center). */
  const fromCenter = useCallback((x: number, y: number) => {
    const r = stage.current?.getBoundingClientRect();
    return r
      ? { x: x - (r.left + r.width / 2), y: y - (r.top + r.height / 2) }
      : { x: 0, y: 0 };
  }, []);

  const limits = useRef(onLimits);
  limits.current = onLimits;
  /** Writes the shown position directly, not through a React render (keeps up with fingers). */
  const paint = useCallback((z: Zoom) => {
    shown.current = z;
    const el = img.current;
    if (el) {
      el.style.transform = `translate(${z.x}px, ${z.y}px) scale(${z.s})`;
      // A layer of its own only while moved from its place: given one from
      // the start, the first picture opened in the iPhone app stayed undrawn
      // (decoded and laid out, the screen empty; 2026-11-02).
      const still = Math.abs(z.s - 1) < 0.001 && z.x === 0 && z.y === 0;
      el.style.willChange = still ? "" : "transform";
    }
    setZoomed(z.s > 1.001);
    limits.current(z.s <= 1.001, z.s >= ZOOM_MAX - 0.001);
  }, []);
  /** Places at an in-range position at once (raw value too). */
  const place = useCallback(
    (z: Zoom) => {
      cancelAnimationFrame(anim.current);
      animating.current = false;
      raw.current = z;
      paint(z);
    },
    [paint],
  );
  /** Animates from what is shown to `to`. */
  const glide = useCallback(
    (to: Zoom, ms = ANIM_MS) => {
      cancelAnimationFrame(anim.current);
      raw.current = to;
      if (reducedMotion()) {
        animating.current = false;
        paint(to);
        return;
      }
      const from = shown.current;
      const start = performance.now();
      animating.current = true;
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / ms);
        paint(mixZoom(from, to, easeOut(t)));
        if (t < 1) anim.current = requestAnimationFrame(step);
        else animating.current = false;
      };
      anim.current = requestAnimationFrame(step);
    },
    [paint],
  );
  /**
   * A finger touching mid-animation stops it where it is seen and grabs from
   * there. Nothing happens when not animating: while pulled out of range,
   * `shown` is the shrunk value, and copying it to raw would make the picture
   * jump inward the moment a second finger lands.
   */
  const stop = useCallback(() => {
    if (!animating.current) return;
    cancelAnimationFrame(anim.current);
    animating.current = false;
    raw.current = shown.current;
  }, []);

  /** Takes the start again from the current fingers (one added or lifted). */
  const regrab = () => {
    const [a, b] = [...pointers.current.values()];
    if (!a) {
      grab.current = null;
      return;
    }
    grab.current = b
      ? {
          zoom: raw.current,
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
          dist: Math.hypot(a.x - b.x, a.y - b.y),
        }
      : { zoom: raw.current, x: a.x, y: a.y, dist: 0 };
    samples.current = [];
  };

  /** Zoom in / back (double tap, double click). */
  const toggleAt = (x: number, y: number) => {
    const z = shown.current;
    glide(
      z.s > 1
        ? ZOOM_IDENTITY
        : zoomAt(z, ZOOM_STEP, fromCenter(x, y), box(), view()),
    );
  };

  /**
   * A sideways swipe at fit size let go: far or fast enough toward an image
   * there, the picture leaves that way and the next one comes in; else it
   * springs back.
   */
  const releaseSwipe = () => {
    const dx = raw.current.x;
    const s = samples.current;
    const first = s[0];
    const last = s[s.length - 1];
    samples.current = [];
    swipe.current = null;
    const speed =
      first && last && last.t > first.t
        ? (last.x - first.x) / (last.t - first.t)
        : 0;
    const by: 1 | -1 = dx < 0 ? 1 : -1;
    const width = view().width;
    const far =
      Math.abs(dx) > width * SWIPE_SHARE ||
      (Math.abs(speed) >= SWIPE_SPEED && Math.sign(speed) === Math.sign(dx));
    if (dx === 0 || !far || !canStep(by)) {
      glide(ZOOM_IDENTITY);
      return;
    }
    if (reducedMotion()) {
      onStep(by, true);
      return;
    }
    glide({ s: 1, x: -by * width, y: 0 }, SWIPE_OUT_MS);
    outTimer.current = window.setTimeout(() => {
      outTimer.current = null;
      onStep(by, true);
    }, SWIPE_OUT_MS);
  };

  /** All fingers lifted: spring back what was pulled, glide if flicked. */
  const release = () => {
    if (swipe.current === "x") {
      releaseSwipe();
      return;
    }
    swipe.current = null;
    const z = raw.current;
    const s = samples.current;
    const first = s[0];
    const last = s[s.length - 1];
    samples.current = [];
    if (first && last && last.t > first.t && z.s > 1) {
      const v = {
        x: (last.x - first.x) / (last.t - first.t),
        y: (last.y - first.y) / (last.t - first.t),
      };
      if (Math.hypot(v.x, v.y) >= FLING_MIN_SPEED) {
        glide(flingZoom(z, v, box(), view()), FLING_ANIM_MS);
        return;
      }
    }
    glide(settleZoom(z, box(), view()));
  };

  /**
   * A finger released without moving. A second tap zooms in / back at once; a
   * first one waits, then toggles the controls.
   */
  const tap = (t: number, x: number, y: number) => {
    const prev = lastTap.current;
    if (
      prev &&
      t - prev.t < DOUBLE_TAP_MS &&
      Math.hypot(x - prev.x, y - prev.y) < DOUBLE_TAP_PX
    ) {
      lastTap.current = null;
      if (tapTimer.current !== null) clearTimeout(tapTimer.current);
      tapTimer.current = null;
      toggleAt(x, y);
      return;
    }
    lastTap.current = { t, x, y };
    if (tapTimer.current !== null) clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      tapTimer.current = null;
      onTap();
    }, DOUBLE_TAP_MS);
  };

  // frontend-development: not a fetch; cleanup of the animation and the timers.
  useEffect(
    () => () => {
      cancelAnimationFrame(anim.current);
      if (tapTimer.current !== null) clearTimeout(tapTimer.current);
      if (outTimer.current !== null) clearTimeout(outTimer.current);
    },
    [],
  );

  // Stepped to: slides in from the side it comes from, fading in.
  // frontend-development: not a fetch; the coming in of a stepped-to image, once.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, as it mounts (each image is its own stage)
  useLayoutEffect(() => {
    const el = img.current;
    if (!enter || !el || reducedMotion()) return;
    const from = enter.swipe
      ? view().width * ENTER_SWIPE_SHARE
      : ENTER_PRESS_PX;
    place({ s: 1, x: enter.by * from, y: 0 });
    glide(ZOOM_IDENTITY, ENTER_MS);
    if (typeof el.animate === "function")
      el.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: ENTER_MS,
        easing: "ease-out",
      });
  }, []);

  // − and + zoom toward the middle of the screen; a new image starts at fit size.
  // frontend-development: not a fetch; hands the zoom to the viewer's buttons.
  useLayoutEffect(() => {
    control.current = {
      zoomBy: (f) => {
        const z = shown.current;
        glide(zoomAt(z, z.s * f, { x: 0, y: 0 }, box(), view()));
      },
      reset: () => glide(ZOOM_IDENTITY),
    };
    limits.current(true, false);
    return () => {
      control.current = null;
    };
  }, [control, glide, box, view]);

  // Read by the wheel listener, which is attached once.
  const stepping = useRef({ canStep, onStep });
  stepping.current = { canStep, onStep };

  // wheel and Safari's gesture events are attached directly: React registers
  // wheel as passive, so its preventDefault would not stop the page zoom.
  // frontend-development: not a fetch; subscribes to wheel and gesture input.
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const z = shown.current;
      // Two fingers sideways on a trackpad step to another image, once per
      // swipe (its glide after the fingers lift does nothing more); not while
      // zoomed in, where they would be looking around the picture.
      const w = wheelSwipe.current;
      const gone = e.timeStamp - w.last > WHEEL_GAP_MS;
      if (gone) {
        w.sum = 0;
        w.done = false;
      }
      const zooms = e.ctrlKey || e.metaKey;
      const sideways = !zooms && Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (w.done && !gone) {
        w.last = e.timeStamp;
        return;
      }
      if (sideways && z.s <= 1.001) {
        w.last = e.timeStamp;
        w.sum += e.deltaMode === 1 ? e.deltaX * 40 : e.deltaX;
        if (Math.abs(w.sum) >= WHEEL_STEP_PX) {
          w.done = true;
          stepping.current.onStep(w.sum > 0 ? 1 : -1);
        }
        return;
      }
      const dx = e.deltaMode === 1 ? e.deltaX * 40 : e.deltaX;
      const dy = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY;
      if (zooms) {
        // Trackpad pinch arrives with ctrl (Chrome / Firefox), in small
        // deltas; a mouse wheel's notch (~100px) is kept to about 20%.
        const by = Math.max(
          -WHEEL_ZOOM_MAX,
          Math.min(WHEEL_ZOOM_MAX, -dy * 0.01),
        );
        place(
          zoomAt(
            z,
            z.s * Math.exp(by),
            fromCenter(e.clientX, e.clientY),
            box(),
            view(),
          ),
        );
        return;
      }
      // The wheel alone moves around the zoomed picture (nothing at fit size).
      if (z.s <= 1.001) return;
      place(clampZoom({ s: z.s, x: z.x - dx, y: z.y - dy }, box(), view()));
    };
    let gestureFrom: Zoom | null = null;
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      gestureFrom = shown.current;
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      // iOS sends the same pinch as pointer events too; don't apply it twice.
      if (pointers.current.size > 0 || !gestureFrom) return;
      const g = e as GestureLike;
      place(
        zoomAt(
          gestureFrom,
          gestureFrom.s * g.scale,
          fromCenter(g.clientX, g.clientY),
          box(),
          view(),
        ),
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("gesturestart", onGestureStart);
    el.addEventListener("gesturechange", onGestureChange);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGestureStart);
      el.removeEventListener("gesturechange", onGestureChange);
    };
  }, [place, fromCenter, box, view, wheelSwipe]);

  /**
   * The size before zooming (`nativePixels`): device pixels at actual size, but
   * no larger than the stage. Kept in width / height, not transform, so `box`
   * measures the unzoomed size.
   */
  const sizeImage = useCallback(() => {
    const el = img.current;
    const st = stage.current;
    if (!el || !st) return;
    if (!nativePixels || !el.naturalWidth || !el.naturalHeight) {
      el.style.width = "";
      el.style.height = "";
      return;
    }
    const s = Math.min(
      SMALL_IMAGE_MAX / (window.devicePixelRatio || 1),
      st.clientWidth / el.naturalWidth,
      st.clientHeight / el.naturalHeight,
    );
    el.style.width = `${el.naturalWidth * s}px`;
    el.style.height = `${el.naturalHeight * s}px`;
  }, [nativePixels]);
  // frontend-development: not a fetch; sizes the image again when the screen class changes.
  useEffect(() => sizeImage(), [sizeImage]);

  // When the screen size changes while zoomed (rotation), only the overflow is pulled back.
  // frontend-development: not a fetch; re-fits the position to a resized screen.
  useEffect(() => {
    const onResize = () => {
      sizeImage();
      place(settleZoom(shown.current, box(), view()));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [place, box, view, sizeImage]);

  return (
    <div
      ref={stage}
      className={`absolute inset-0 flex items-center justify-center ${
        // A click closes (while zoomed it stays a grab to pan).
        zoomed ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-out"
      }`}
      // The page's own zoom and scroll stop; every finger move is handled here.
      style={{ touchAction: "none" }}
      onPointerDown={(e) => {
        // Mouse: left button only. A context menu may swallow pointerup, and a
        // pointer left "down" would keep moving the picture after release.
        if (e.pointerType === "mouse" && e.button !== 0) return;
        // Swiped away already: the next image is on its way.
        if (outTimer.current !== null) return;
        stop();
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.current.size === 1) {
          moved.current = false;
          downAt.current = { x: e.clientX, y: e.clientY };
        } else moved.current = true;
        // A second finger makes it a pinch, not a swipe.
        swipe.current = null;
        regrab();
      }}
      onPointerMove={(e) => {
        if (!pointers.current.has(e.pointerId)) return;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const g = grab.current;
        if (!g) return;
        const d = downAt.current;
        if (
          !moved.current &&
          d &&
          Math.hypot(e.clientX - d.x, e.clientY - d.y) > MOVE_SLOP_PX
        ) {
          moved.current = true;
          // Once moving, capture the pointer (it keeps arriving outside the image).
          stage.current?.setPointerCapture?.(e.pointerId);
        }
        const [a, b] = [...pointers.current.values()];
        if (!a) return;
        if (b && g.dist > 0) {
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          // The picture under the starting midpoint follows the current
          // midpoint while the zoom changes.
          const at = fromCenter(g.x, g.y);
          const now = fromCenter(mx, my);
          const scaled = zoomAtFree(g.zoom, (g.zoom.s * dist) / g.dist, at);
          raw.current = {
            s: scaled.s,
            x: scaled.x + (now.x - at.x),
            y: scaled.y + (now.y - at.y),
          };
          paint(rubberZoom(raw.current, box(), view()));
          return;
        }
        if (!moved.current) return;
        const t = e.timeStamp;
        // At fit size one finger does not pan (not mistaken for a close or
        // page swipe); sideways, with images around, it swipes to the next.
        if (g.zoom.s <= 1) {
          if (e.pointerType === "mouse" || (!canStep(1) && !canStep(-1)) || !d)
            return;
          swipe.current ??=
            Math.abs(a.x - d.x) > Math.abs(a.y - d.y) ? "x" : "none";
          if (swipe.current !== "x") return;
          const dx = a.x - g.x;
          raw.current = { s: 1, x: dx, y: 0 };
          paint({
            s: 1,
            x: canStep(dx < 0 ? 1 : -1) ? dx : dx * END_PULL,
            y: 0,
          });
          samples.current = [
            ...samples.current.filter((p) => t - p.t <= FLING_WINDOW_MS),
            { x: a.x, y: a.y, t },
          ];
          return;
        }
        raw.current = {
          s: g.zoom.s,
          x: g.zoom.x + (a.x - g.x),
          y: g.zoom.y + (a.y - g.y),
        };
        paint(rubberZoom(raw.current, box(), view()));
        samples.current = [
          ...samples.current.filter((p) => t - p.t <= FLING_WINDOW_MS),
          { x: a.x, y: a.y, t },
        ];
      }}
      onPointerUp={(e) => {
        if (!pointers.current.delete(e.pointerId)) return;
        if (pointers.current.size > 0) {
          // Two fingers to one: the remaining finger keeps moving (flick speed measured afresh).
          regrab();
          return;
        }
        grab.current = null;
        if (moved.current) {
          release();
          return;
        }
        // A mouse click, on the image or around it, closes at once. Waiting to
        // see whether it is a double click felt like lag before closing (user
        // feedback).
        if (e.pointerType === "mouse") {
          onMouseClick();
          return;
        }
        tap(e.timeStamp, e.clientX, e.clientY);
      }}
      onPointerCancel={(e) => {
        pointers.current.delete(e.pointerId);
        if (pointers.current.size > 0) regrab();
        else {
          grab.current = null;
          samples.current = [];
          if (swipe.current === "x") raw.current = ZOOM_IDENTITY;
          swipe.current = null;
          glide(settleZoom(raw.current, box(), view()));
        }
      }}
    >
      {/* The image itself. No surface: `max-h` / `max-w` alone fit it to the
          screen (small images are not stretched). On desktop it is sized to
          device pixels once loaded (`sizeImage`). Zoom is transform only, so
          `offsetWidth` keeps measuring the unzoomed size. */}
      <img
        ref={img}
        src={src}
        alt={name}
        draggable={false}
        onLoad={sizeImage}
        className="relative max-h-full max-w-full select-none object-contain"
        style={{ transform: "translate(0px, 0px) scale(1)" }}
      />
    </div>
  );
}
